import {isFeatureEnabled} from '@github-ui/feature-flags'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import type {
  CookieConsentItem,
  FooterLink,
  FooterLinkSection,
  LocaleOption,
  Newsletter,
  SocialLink,
} from './marketing-footer.types'

export const ARIA_HEADING = (): string => t('Site-wide Links')
export const LEGAL_NAV_LABEL = (): string => t('Legal and Resource Links')
export const SOCIAL_NAV_LABEL = (): string => t("GitHub's Social Media Links")
export const SELECT_LANGUAGE_LABEL = (): string => t('Select language')
export const STAFF_TOGGLES_NAV_LABEL = (): string => t('Staff toggles')
export const SITE_ADMIN_LABEL = (): string => t('Site admin mode')
export const UI_SERVICE_LABEL = (): string => t('UI service')
export const STATE_ON_LABEL = (): string => t('on')
export const STATE_OFF_LABEL = (): string => t('off')
export const SITE_ADMIN_TURN_OFF_LABEL = (): string => t('Turn off site admin mode (currently on)')
export const SITE_ADMIN_TURN_ON_LABEL = (): string => t('Turn on site admin mode (currently off)')
export const UI_SERVICE_OPT_IN_LABEL = (): string => t('Opt in to UI service (currently opted out)')
export const UI_SERVICE_OPT_OUT_LABEL = (): string => t('Opt out of UI service (currently opted in)')

export const newsletter = (): Newsletter => ({
  heading: t('The developer newsletter'),
  description: t('Get tips, technical guides, and best practices. Twice a month. Right in your inbox.'),
  ctaLabel: t('Subscribe'),
  ctaHref: '/newsletter',
  logoAriaLabel: t('Go to GitHub homepage'),
  logoHref: '/',
})

const link = (label: string, href: string, analyticsAction: string): FooterLink => ({
  label,
  href,
  analyticsAction,
})

const legacySections = (): FooterLinkSection[] => [
  {
    id: 'footer-title-platform',
    title: t('Platform'),
    context: 'platform',
    links: [
      link(t('Features'), '/features', 'features'),
      link(t('Enterprise'), '/enterprise', 'enterprise'),
      link(t('Copilot'), '/features/copilot', 'copilot'),
      link(t('AI'), '/features/ai', 'ai'),
      link(t('Security'), '/security', 'security'),
      link(t('Pricing'), '/pricing', 'pricing'),
      link(t('Team'), '/team', 'team'),
      link(t('Resources'), 'https://github.com/resources', 'resources'),
      link(t('Roadmap'), '/github/roadmap', 'roadmap'),
      link(t('Compare GitHub'), '/resources/articles/devops-tools-comparison', 'compare'),
    ],
  },
  {
    id: 'footer-title-ecosystem',
    title: t('Ecosystem'),
    context: 'ecosystem',
    links: [
      link(t('Developer API'), 'https://docs.github.com/integrations/concepts/about-building-integrations', 'dev-api'),
      link(t('Partners'), 'https://github.com/partners', 'partners'),
      link(t('Education'), '/education', 'edu'),
      link('GitHub CLI', 'https://cli.github.com', 'cli'),
      link('GitHub Desktop', 'https://github.com/apps/desktop', 'desktop'),
      link('GitHub Mobile', '/mobile', 'mobile'),
      link('GitHub Marketplace', '/marketplace', 'marketplace'),
      link('MCP Registry', '/mcp', 'mcp_registry'),
    ],
  },
  {
    id: 'footer-title-support',
    title: t('Support'),
    context: 'support',
    links: [
      link(t('Docs'), 'https://docs.github.com', 'docs'),
      link(t('Community Forum'), 'https://github.community', 'community'),
      link(t('Professional Services'), 'https://github.com/services', 'services'),
      link(t('Premium Support'), '/enterprise/premium-support', 'premium_support'),
      link(t('Skills'), 'https://skills.github.com', 'skills'),
      link(t('Status'), 'https://www.githubstatus.com', 'status'),
      link(t('Contact GitHub'), 'https://support.github.com?tags=dotcom-footer', 'contact_github'),
      link(t('What is Git?'), '/git-guides', 'what_is_git'),
      link(t('Sitemap'), '/sitemap', 'sitemap'),
    ],
  },
  {
    id: 'footer-title-company',
    title: t('Company'),
    context: 'company',
    links: [
      link(t('About'), '/about', 'about'),
      link(t('Why GitHub'), '/why-github', 'why_github'),
      link(t('Customer Stories'), '/customer-stories?type=enterprise', 'customer_stories'),
      link(t('Blog'), 'https://github.blog', 'blog'),
      link(t('The ReadME Project'), '/readme', 'readme'),
      link(t('Careers'), 'https://github.careers', 'careers'),
      link(t('Inclusion'), '/about/diversity', 'inclusion'),
      link(t('Social Impact'), 'https://socialimpact.github.com', 'social_impact'),
      link(t('Shop'), 'https://thegithubshop.com', 'shop'),
    ],
  },
]

export function sections(): FooterLinkSection[] {
  if (!isFeatureEnabled('site_marketing_footer_new_ia')) return legacySections()

  return [
    {
      id: 'footer-title-products',
      title: t('Products'),
      context: 'products',
      links: [
        link(t('GitHub Platform'), '/platform', 'platform'),
        link(t('Actions'), '/features/actions', 'actions'),
        link(t('Code Quality'), '/features/code-quality', 'code_quality'),
        link(t('Copilot'), '/features/copilot', 'copilot'),
        link(t('Codespaces'), '/features/codespaces', 'codespaces'),
        link(t('Code Security'), '/security/advanced-security/code-security', 'code_security'),
        link(t('Secret Protection'), '/security/advanced-security/secret-protection', 'secret_protection'),
        link(t('Pricing'), '/pricing', 'pricing'),
        link(t('Team'), '/team', 'team'),
        link(t('Resources'), 'https://github.com/resources', 'resources'),
        link(t('Roadmap'), '/github-roadmap', 'roadmap'),
        link(t('Compare GitHub'), '/resources/articles/devops-tools-comparison', 'compare'),
        link(t('All Features'), '/features', 'features'),
      ],
    },
    {
      id: 'footer-title-developers',
      title: t('Developers'),
      context: 'developers',
      links: [
        link(
          t('Developer API'),
          'https://docs.github.com/integrations/concepts/about-building-integrations',
          'dev-api',
        ),
        link(t('Education'), '/education', 'edu'),
        link('GitHub CLI', 'https://cli.github.com', 'cli'),
        link('GitHub Desktop', 'https://github.com/apps/desktop', 'desktop'),
        link('GitHub Mobile', '/mobile', 'mobile'),
        link(t('Integrations'), '/marketplace', 'marketplace'),
        link('MCP Registry', '/mcp', 'mcp_registry'),
        link(t('Training'), 'https://skills.github.com', 'skills'),
        link(t('Build with Copilot'), '/features/ai', 'ai'),
      ],
    },
    {
      id: 'footer-title-support',
      title: t('Support'),
      context: 'support',
      links: [
        link(t('Docs'), 'https://docs.github.com', 'docs'),
        link(t('Community Forum'), 'https://github.community', 'community'),
        link(t('Professional Services'), 'https://github.com/services', 'services'),
        link(t('Premium Support'), '/enterprise/premium-support', 'premium_support'),
        link(t('GitHub Status'), 'https://www.githubstatus.com', 'status'),
        link(t('Contact GitHub'), 'https://support.github.com?tags=dotcom-footer', 'contact_github'),
        link(t('Sitemap'), '/sitemap', 'sitemap'),
      ],
    },
    {
      id: 'footer-title-company',
      title: t('Company'),
      context: 'company',
      links: [
        link(t('About'), '/about', 'about'),
        link(t('Why GitHub'), '/why-github', 'why_github'),
        link(t('Customer Stories'), '/customer-stories?type=enterprise', 'customer_stories'),
        link(t('Blog'), 'https://github.blog', 'blog'),
        link(t('Careers'), 'https://github.careers', 'careers'),
        link(t('Inclusion'), '/about/diversity', 'inclusion'),
        link(t('Partners'), 'https://github.com/partners', 'partners'),
        link(t('Social Impact'), 'https://socialimpact.github.com', 'social_impact'),
        link(t('Shop'), 'https://thegithubshop.com', 'shop'),
        link(t('What is Git?'), '/git-guides', 'what_is_git'),
      ],
    },
  ]
}

export const subFooterLegalLinks = (): FooterLink[] => [
  link(t('Terms'), 'https://docs.github.com/site-policy/github-terms/github-terms-of-service', 'terms'),
  link(
    t('Privacy'),
    'https://docs.github.com/site-policy/privacy-policies/github-general-privacy-statement',
    'privacy',
  ),
]

export const cookieConsentItems = (): CookieConsentItem[] => [
  {label: t('Manage cookies'), analyticsAction: 'cookies'},
  {label: t('Do not share my personal information'), analyticsAction: 'dont_share_info'},
]

export const socialLinks = (): SocialLink[] => [
  {
    srLabel: t('GitHub on LinkedIn'),
    analyticsAction: 'go to Linkedin',
    analyticsLabel: 'text:linkedin',
    url: 'https://www.linkedin.com/company/github',
    iconName: 'linkedin',
  },
  {
    srLabel: t('GitHub on Instagram'),
    analyticsAction: 'go to Instagram',
    analyticsLabel: 'text:instagram',
    url: 'https://www.instagram.com/github',
    iconName: 'instagram',
  },
  {
    srLabel: t('GitHub on YouTube'),
    analyticsAction: 'go to YouTube',
    analyticsLabel: 'text:youtube',
    url: 'https://www.youtube.com/github',
    iconName: 'youtube',
  },
  {
    srLabel: t('GitHub on X'),
    analyticsAction: 'go to X',
    analyticsLabel: 'text:x',
    url: 'https://x.com/github',
    iconName: 'x',
  },
  {
    srLabel: t('GitHub on TikTok'),
    analyticsAction: 'go to tiktok',
    analyticsLabel: 'text:tiktok',
    url: 'https://www.tiktok.com/@github',
    iconName: 'tiktok',
  },
  {
    srLabel: t('GitHub on Twitch'),
    analyticsAction: 'go to Twitch',
    analyticsLabel: 'text:twitch',
    url: 'https://www.twitch.tv/github',
    iconName: 'twitch',
  },
  {
    srLabel: t('GitHub\u2019s organization on GitHub'),
    analyticsAction: "go to github's org",
    analyticsLabel: 'text:github',
    url: 'https://github.com/github',
    iconName: 'github',
  },
]

/**
 * Fallback locale list used when Rails does not pass `localeOptions`. The authoritative,
 * per-user feature-flagged list comes from `MarketingLocales.enabled_selector_links` server-side.
 */
export const defaultLocaleOptions = (): LocaleOption[] => [
  {label: 'English', locale: 'en-us', analyticsLabel: 'text:english'},
]
